// Sign-your-name section. Three states on one section (data-state):
//   intro: title, "Sign your name" button and recent names
//   form:  name field, Submit, and a live script preview as you type
//   wall:  names with this browser's own names first, paged with More, then Previous / Next
// Names are saved and read through public/api/signatures.php.

const API_URL = './api/signatures.php';
const INTRO_COUNT = 12; // design shows 6 rows x 2 columns on the intro
const WALL_COUNT = 16; // design shows 8 rows x 2 columns on the wall
const MAX_NAME_LENGTH = 40; // must match SIG_MAX_NAME_LENGTH in phplib/signatures.php
// Letters (any language), spaces, apostrophes, periods and hyphens. Must match phplib/signatures.php.
const NAME_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M}\s'’.-]*$/u;
// Anything NAME_PATTERN doesn't allow (digits, symbols, emoji). These can't be typed or pasted into the field.
const NOT_NAME_CHARACTER = /[^\p{L}\p{M} '’.-]/u;
// A period followed by two or more letters looks like a web address. Must match phplib/signatures.php.
const DOMAIN_PATTERN = /\.\p{L}{2,}/u;
// Browser cookie with the ids of names signed here, so they show first. There are no accounts.
const COOKIE_NAME = 'lbn_signatures';
const COOKIE_DAYS = 365; // the site runs for a season; a year outlasts it
const MAX_REMEMBERED = 20; // must match SIG_MAX_MINE in phplib/signatures.php
const VIEW_THRESHOLD = 0.4; // share of the section on screen before it animates in and counts as seen
const FADE_MS = 300; // matches $signature-fade in _signature.scss
const NAME_STAGGER_MS = 40; // names rise in one after another: 16 names take about 0.6s
const WALL_ENTER_DELAY_MS = 600; // after Submit, wait for the wall panel to fade in (two $signature-fade)
const WRITE_LEAD_MS = 300; // your new name starts writing this long before the others rise in
const DESIGN_HEIGHT = 802; // Figma frame height; matches $signature-frame-height in _signature.scss

const MESSAGES = {
  invalid: 'Please enter your name using letters only, up to 40 characters.',
  not_allowed: 'That name cannot be added. Please try a different one.',
  rate_limited: 'Please wait a few minutes before signing again.',
  unavailable: 'Your name could not be saved. Please try again later.',
};

const signatureModule = {
  init(root = document.querySelector('[data-signature]')) {
    if (!root) return;
    root.classList.add('signature--js');

    // On the main page the section is a page in the strip, drawn like the Figma frame and
    // scaled to the strip's height. The styles only use this on desktop.
    const page = root.closest('.page');
    if (page) {
      const fit = () => page.style.setProperty('--signature-scale', page.clientHeight / DESIGN_HEIGHT);
      new ResizeObserver(fit).observe(page);
      fit();
    }

    const panels = Array.from(root.querySelectorAll('[data-signature-panel]'));
    const title = root.querySelector('[data-signature-title]');
    const introList = root.querySelector('[data-signature-list="intro"]');
    const wallList = root.querySelector('[data-signature-list="wall"]');
    const form = root.querySelector('[data-signature-form]');
    const input = root.querySelector('[data-signature-input]');
    const submit = form.querySelector('[type="submit"]');
    const preview = root.querySelector('[data-signature-preview]');
    const error = root.querySelector('[data-signature-error]');
    const pager = root.querySelector('.signature__pager');
    const more = root.querySelector('[data-signature-more]');
    const previous = root.querySelector('[data-signature-previous]');
    const next = root.querySelector('[data-signature-next]');
    const again = root.querySelector('[data-signature-again]');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

    let wallPage = 1;
    let wallPages = 1;

    const setState = state => {
      root.dataset.state = state;
      panels.forEach(panel => {
        panel.inert = panel.dataset.signaturePanel !== state;
      });
    };

    // Without options, names appear at once. With them, names rise in one after another
    // (starting after `delay` ms), and the name with id `writeId` writes itself in first.
    const render = (list, names, { animate = false, delay = 0, writeId = null } = {}) => {
      let order = 0;
      list.replaceChildren(...names.map(item => {
        const li = document.createElement('li');
        li.className = 'signature__name';
        if (animate && item.id === writeId) {
          const ink = document.createElement('span');
          ink.className = 'signature__name-ink';
          ink.textContent = item.name;
          li.append(ink);
          li.classList.add('signature__name--write');
          li.style.setProperty('--signature-delay', `${delay}ms`);
          return li;
        }
        li.textContent = item.name;
        if (animate) {
          const lead = writeId === null ? 0 : WRITE_LEAD_MS;
          li.classList.add('signature__name--enter');
          li.style.setProperty('--signature-delay', `${delay + lead + order * NAME_STAGGER_MS}ms`);
          order++;
        }
        return li;
      }));
    };

    // Fade the old names out, then bring the new ones in one after another.
    const swap = async (list, names, options = {}) => {
      const animate = !reducedMotion.matches;
      if (animate && list.children.length) {
        list.classList.add('is-changing');
        await new Promise(resolve => setTimeout(resolve, FADE_MS));
      }
      render(list, names, { ...options, animate });
      list.classList.remove('is-changing');
    };

    const loadWall = async (page, options = {}) => {
      const pagerHadFocus = pager.contains(document.activeElement);
      const data = await this.request({ action: 'list', page, perPage: WALL_COUNT, mine: this.readMine() });
      if (!data.ok) return;
      wallPage = data.page;
      wallPages = data.pages;
      await swap(wallList, data.names, options);

      // Page 1 shows More. After that, Previous and Next move back and forth.
      more.hidden = !(wallPage === 1 && wallPages > 1);
      previous.hidden = wallPage === 1;
      next.hidden = wallPage === 1 || wallPage >= wallPages;
      pager.hidden = more.hidden && previous.hidden && next.hidden;

      // A button that hides itself (More, or Next on the last page) drops focus; hand it to the next visible one.
      if (pagerHadFocus && (!pager.contains(document.activeElement) || document.activeElement.hidden)) {
        (pager.querySelector('button:not([hidden])') || title).focus();
      }
    };

    const openForm = () => {
      setState('form');
      error.textContent = '';
      input.focus();
    };

    root.querySelector('[data-signature-start]').addEventListener('click', () => {
      this.track('start');
      openForm();
    });

    again.addEventListener('click', openForm);

    // Drop anything a name can't contain and any doubled space, keeping the cursor where it was.
    const keepNameCharacters = () => {
      const notAllowed = new RegExp(NOT_NAME_CHARACTER.source, 'gu');
      const clean = text => text.replace(notAllowed, '').replace(/ {2,}/g, ' ');
      const cleaned = clean(input.value);
      if (cleaned === input.value) return;
      const caret = clean(input.value.slice(0, input.selectionStart ?? input.value.length)).length;
      input.value = cleaned;
      input.setSelectionRange(caret, caret);
    };

    // The preview stays on one line: a name wider than its box shrinks until it fits.
    const fitPreview = () => {
      preview.style.removeProperty('--signature-preview-fit');
      const overflow = preview.scrollWidth / preview.clientWidth;
      if (overflow > 1) preview.style.setProperty('--signature-preview-fit', 1 / overflow);
    };

    // Runs on every change (typing, pasting, autofill, dictation) before the screen redraws,
    // so a digit or symbol never shows up in the field.
    const showPreview = () => {
      keepNameCharacters();
      preview.textContent = input.value.trim();
      fitPreview();
      error.textContent = '';
    };

    input.addEventListener('input', event => {
      if (!event.isComposing) showPreview(); // wait until an accent or IME character is finished
    });
    input.addEventListener('compositionend', showPreview);

    form.addEventListener('submit', async event => {
      event.preventDefault();
      const name = input.value.trim().replace(/\s+/g, ' ');
      if (!name || [...name].length > MAX_NAME_LENGTH || !NAME_PATTERN.test(name) || DOMAIN_PATTERN.test(name)) {
        error.textContent = MESSAGES.invalid;
        input.focus();
        return;
      }

      submit.disabled = true;
      const data = await this.request({ action: 'add', name });
      submit.disabled = false;
      if (!data.ok) {
        error.textContent = MESSAGES[data.error] || MESSAGES.unavailable;
        input.focus();
        return;
      }

      this.remember(data.id);
      form.reset();
      preview.textContent = '';
      wallList.replaceChildren();
      setState('wall');
      title.focus();
      await loadWall(1, { delay: WALL_ENTER_DELAY_MS, writeId: data.id });
    });

    more.addEventListener('click', () => loadWall(2));
    next.addEventListener('click', () => loadWall(wallPage + 1));
    previous.addEventListener('click', () => loadWall(wallPage - 1));

    setState(root.dataset.state || 'intro');
    this.request({ action: 'list', page: 1, perPage: INTRO_COUNT, mine: this.readMine() }).then(data => {
      if (data.ok) render(introList, data.names);
    });

    // Animate in and count a view the first time the section is on screen.
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      root.classList.add('is-visible');
      this.track('view');
    }, { threshold: VIEW_THRESHOLD });
    observer.observe(root);
  },

  readMine() {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
      const ids = match ? JSON.parse(decodeURIComponent(match[1])) : [];
      return Array.isArray(ids) ? ids.filter(Number.isInteger).slice(0, MAX_REMEMBERED) : [];
    } catch {
      return [];
    }
  },

  remember(id) {
    if (!Number.isInteger(id)) return;
    const ids = [id, ...this.readMine().filter(existing => existing !== id)].slice(0, MAX_REMEMBERED);
    const secure = location.protocol === 'https:' ? '; secure' : '';
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(ids))}; max-age=${COOKIE_DAYS * 86400}; path=/; samesite=lax${secure}`;
  },

  track(event) {
    this.request({ action: 'track', event });
  },

  async request(body) {
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return await response.json();
    } catch {
      return { ok: false, error: 'unavailable' };
    }
  },
};

export default signatureModule;
